import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { TbLogin2, TbMapPin, TbUsers } from "react-icons/tb";
import type { BookingDetailResult } from "src/store/slices/hotels/Hotels";

const StayChip: FC<{ title: ReactNode; subText: ReactNode; subTitle: ReactNode; icon: ReactNode }> = ({ title, subText, subTitle, icon }) => {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3 px-5 py-1">
      <div className="bg-background-primary-lighter text-font-secondary flex size-10 shrink-0 items-center justify-center rounded-md text-xl">{icon}</div>
      <div className="flex min-w-0 flex-col">
        <div className="text-font-subtle text-xs uppercase">{title}</div>
        <div className="text-font-default truncate text-base font-semibold">{subText}</div>
        <div className="text-font-subtle truncate text-sm">{subTitle}</div>
      </div>
    </div>
  );
};

const formatStayRange = (checkIn: dayjs.Dayjs, checkOut: dayjs.Dayjs) => {
  const startFormat = checkIn.isSame(checkOut, "year") ? "MMM D" : "MMM D, YYYY";
  return `${checkIn.format(startFormat)} - ${checkOut.format("MMM D, YYYY")}`;
};

const StayChips: FC<{ bookingDetail: BookingDetailResult }> = ({ bookingDetail }) => {
  const { t } = useTranslation();
  const { hotelDetails, rooms, noOfRooms } = bookingDetail;

  const checkIn = dayjs(bookingDetail.checkIn);
  const checkOut = dayjs(bookingDetail.checkOut);
  const nights = checkOut.startOf("day").diff(checkIn.startOf("day"), "day");

  const guestsCount = rooms.reduce(
    (total, room) => total + room.customerDetails.reduce((roomTotal, detail) => roomTotal + (detail.customerNames?.length ?? 0), 0),
    0,
  );
  const roomName = rooms[0]?.name?.[0] ?? "";
  const roomsCount = noOfRooms || rooms.length;

  return (
    <div className="bg-background-default border-border-subtle flex w-full flex-col divide-y divide-border-subtle rounded-sm border shadow-xs md:flex-row md:divide-x md:divide-y-0 p-2">
      <StayChip
        icon={<TbMapPin className="text-font-active"/>}
        title={t("BookingResult.stayChips.hotel")}
        subText={hotelDetails.hotelName}
        subTitle={hotelDetails.city}
      />
      <StayChip
        icon={<TbLogin2 className="text-font-active"/>}
        title={t("BookingResult.stayChips.stay")}
        subText={formatStayRange(checkIn, checkOut)}
        subTitle={t("BookingResult.stayChips.nights", { count: nights })}
      />
      <StayChip
        icon={<TbUsers className="text-font-active"/>}
        title={t("BookingResult.stayChips.guests")}
        subText={t("BookingResult.stayChips.guestsCount", { count: guestsCount })}
        subTitle={roomsCount > 1 ? t("BookingResult.stayChips.roomsCount", { count: roomsCount, room: roomName }) : roomName}
      />
    </div>
  );
};

export default StayChips;
